import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import showToast from '../../components/Toast';
import './BuilderJSEmailBuilder.css';

const BuilderJSEmailBuilder = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const builderContainerRef = useRef(null);
  const settingsContainerRef = useRef(null);
  const widgetsContainerRef = useRef(null);
  const builderInstanceRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [builderReady, setBuilderReady] = useState(false);
  const BASE_URL = config.api.baseURL;
  const API_PATH = config.api.apiPath;

  // Load BuilderJS scripts and styles
  useEffect(() => {
    const loadBuilderJS = () => {
      // Check if BuilderJS is already loaded
      if (window.Builder) {
        initializeBuilder();
        return;
      }

      // Load CSS
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/builderjs/builder.css';
      document.head.appendChild(link);

      // Load JS
      const script = document.createElement('script');
      script.src = '/builderjs/builder.js';
      script.async = true;
      script.onload = () => {
        if (window.Builder) {
          initializeBuilder();
        } else {
          showToast.error('Failed to load BuilderJS library');
          setIsLoading(false);
        }
      };
      script.onerror = () => {
        showToast.error('Failed to load BuilderJS library. Please check if the files are in the public folder.');
        setIsLoading(false);
      };
      document.body.appendChild(script);
    };

    loadBuilderJS();

    // Cleanup on unmount
    return () => {
      // Clean up global builder reference
      if (window.builder === builderInstanceRef.current) {
        delete window.builder;
      }
      if (window.sidebarTabManager) {
        delete window.sidebarTabManager;
      }
    };
  }, []);

  // Initialize BuilderJS
  const initializeBuilder = () => {
    if (!builderContainerRef.current || !settingsContainerRef.current || !widgetsContainerRef.current) {
      console.error('Builder containers not found');
      setIsLoading(false);
      return;
    }

    try {
      // Initialize BuilderJS
      const builder = new window.Builder({
        mainContainer: `#${builderContainerRef.current.id}`,
        settingContainer: `#${settingsContainerRef.current.id}`,
        widgetsContainer: `#${widgetsContainerRef.current.id}`,
        themeUrl: '/themes/standard/',
        // Optional: asset upload handler for images
        // assetUploadHandler: `${BASE_URL}/${API_PATH}/cdn/upload`,
      });

      builderInstanceRef.current = builder;
      
      // Make builder instance available globally (required by widgets)
      // ES modules need to access builder via window.builder
      window.builder = builder;

      // Load template if editing, otherwise load blank theme
      if (templateId) {
        loadTemplate(templateId);
      } else {
        // Load blank theme with empty data
        const blankThemeUrl = '/themes/standard/Y0_Blank';
        const emptyData = {
          theme: 'Blank',
          elementLists: [], // PageElement.parse expects elementLists array
          formats: {} // Optional formats
        };
        
        builder.load(emptyData, blankThemeUrl, () => {
          // Set up sidebarTabManager if it exists
          if (builder.settingsTabManager && !window.sidebarTabManager) {
            window.sidebarTabManager = builder.settingsTabManager;
          }
          
          setBuilderReady(true);
          setIsLoading(false);
        });
      }

    } catch (error) {
      console.error('Error initializing BuilderJS:', error);
      showToast.error('Failed to initialize BuilderJS');
      setIsLoading(false);
    }
  };

  // Load template from API
  const loadTemplate = async (id) => {
    try {
      const response = await AxiosInstance.get(`/templates/by-template-id/${id}`);
      
      if (response.data?.statusCode === 200 && response.data?.data) {
        const template = response.data.data;
        
        // Set template name
        if (template.templateName) {
          setTemplateName(template.templateName);
        }

        // Load JSON into builder
        if (template.json && builderInstanceRef.current) {
          try {
            const jsonData = typeof template.json === 'string' 
              ? JSON.parse(template.json) 
              : template.json;
            
            // Determine theme URL from data or use default
            const themeName = jsonData.theme || 'Y0_Blank';
            const themeUrl = `/themes/standard/${themeName}`;
            
            builderInstanceRef.current.load(jsonData, themeUrl, () => {
              // Set up sidebarTabManager if it exists
              if (builderInstanceRef.current.settingsTabManager && !window.sidebarTabManager) {
                window.sidebarTabManager = builderInstanceRef.current.settingsTabManager;
              }
              
              setBuilderReady(true);
              setIsLoading(false);
              showToast.success('Template loaded successfully');
            });
          } catch (parseError) {
            console.error('Error parsing template JSON:', parseError);
            showToast.error('Failed to parse template data');
            // Initialize with blank theme as fallback
            const blankThemeUrl = '/themes/standard/Y0_Blank';
            const emptyData = { 
              theme: 'Blank', 
              elementLists: [], // PageElement.parse expects elementLists array
              formats: {} 
            };
            builderInstanceRef.current.load(emptyData, blankThemeUrl, () => {
              setBuilderReady(true);
              setIsLoading(false);
            });
          }
        } else {
          // No JSON data, initialize with blank theme
          const blankThemeUrl = '/themes/standard/Y0_Blank';
          const emptyData = { 
            theme: 'Blank', 
            elementLists: [], // PageElement.parse expects elementLists array
            formats: {} 
          };
          builderInstanceRef.current.load(emptyData, blankThemeUrl, () => {
            setBuilderReady(true);
            setIsLoading(false);
          });
        }
      } else {
        showToast.error('Template not found');
        // Initialize with blank theme
        if (builderInstanceRef.current) {
          const blankThemeUrl = '/themes/standard/Y0_Blank';
          const emptyData = { 
            theme: 'Blank', 
            elementLists: [], // PageElement.parse expects elementLists array
            formats: {} 
          };
          builderInstanceRef.current.load(emptyData, blankThemeUrl, () => {
            setBuilderReady(true);
            setIsLoading(false);
          });
        }
      }
    } catch (error) {
      console.error('Error loading template:', error);
      showToast.error('Failed to load template');
      // Initialize with blank theme as fallback
      if (builderInstanceRef.current) {
        const blankThemeUrl = '/themes/standard/Y0_Blank';
        const emptyData = { 
          theme: 'Blank', 
          elementLists: [], // PageElement.parse expects elementLists array
          formats: {} 
        };
        builderInstanceRef.current.load(emptyData, blankThemeUrl, () => {
          setBuilderReady(true);
          setIsLoading(false);
        });
      }
    }
  };

  // Handle save operation
  const handleSave = async (jsonData, htmlData) => {
    if (!templateName.trim()) {
      setShowSaveModal(true);
      return { success: false, message: 'Template name is required' };
    }

    setIsSaving(true);
    try {
      const userId = config.getUserId();
      const requestData = {
        userId,
        templateName: templateName.trim(),
        html: htmlData || builderInstanceRef.current?.getHtml() || '',
        json: jsonData || (builderInstanceRef.current ? JSON.stringify(builderInstanceRef.current.getData()) : '{}'),
      };

      let response;
      if (templateId) {
        // Update existing template
        response = await AxiosInstance.put(`/templates/${templateId}`, requestData);
      } else {
        // Create new template
        response = await AxiosInstance.post('/templates', requestData);
      }

      if (response.data?.statusCode === 200) {
        showToast.success('Template saved successfully');
        setIsSaving(false);
        
        // If this was a new template, navigate to the edit URL
        if (!templateId && response.data?.data?.TemplateId) {
          navigate(`/builderjs-email-builder/${response.data.data.TemplateId}`, { replace: true });
        }
        
        return { success: true, message: 'Template saved successfully' };
      } else {
        throw new Error(response.data?.message || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save template';
      showToast.error(errorMessage);
      setIsSaving(false);
      return { success: false, message: errorMessage };
    }
  };

  // Handle export operation
  const handleExport = async () => {
    if (!builderInstanceRef.current) {
      showToast.error('Builder is not initialized');
      return;
    }

    try {
      const html = builderInstanceRef.current.getHtml();
      const json = builderInstanceRef.current.getJson();

      // Create a blob and download
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${templateName || 'email-template'}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showToast.success('Template exported successfully');
    } catch (error) {
      console.error('Error exporting template:', error);
      showToast.error('Failed to export template');
    }
  };

  // Manual save handler
  const handleManualSave = async () => {
    if (!builderInstanceRef.current) {
      showToast.error('Builder is not initialized');
      return;
    }

    if (!templateName.trim()) {
      setShowSaveModal(true);
      return;
    }

    const jsonData = builderInstanceRef.current.getData();
    const json = JSON.stringify(jsonData);
    const html = builderInstanceRef.current.getHtml();
    
    await handleSave(json, html);
  };

  // Save modal handlers
  const handleSaveModalSave = async () => {
    if (!templateName.trim()) {
      showToast.error('Please enter a template name');
      return;
    }

    setShowSaveModal(false);
    await handleManualSave();
  };

  return (
    <div className="builderjs-email-builder">
      {/* Header Toolbar */}
      <div className="builderjs-toolbar">
        <div className="builderjs-toolbar-left">
          <button 
            className="builderjs-btn builderjs-btn-secondary"
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
          <h2 className="builderjs-title">
            {templateId ? 'Edit Template' : 'New Email Template'}
          </h2>
        </div>
        <div className="builderjs-toolbar-right">
          <input
            type="text"
            className="builderjs-template-name-input"
            placeholder="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleManualSave();
              }
            }}
          />
          <button
            className="builderjs-btn builderjs-btn-primary"
            onClick={handleManualSave}
            disabled={isSaving || !builderReady}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            className="builderjs-btn builderjs-btn-secondary"
            onClick={handleExport}
            disabled={!builderReady}
          >
            Export HTML
          </button>
        </div>
      </div>

      {/* Builder Container */}
      <div className="builderjs-container">
        {isLoading && (
          <div className="builderjs-loading">
            <div className="builderjs-spinner"></div>
            <p>Loading BuilderJS...</p>
          </div>
        )}
        
        {/* Main Builder Area - Center */}
        <div 
          id="builderjs-main-container"
          ref={builderContainerRef}
          className="builderjs-main-container"
          style={{ display: isLoading ? 'none' : 'block' }}
        />

        {/* Widgets Container - Right Side */}
        <div 
          id="builderjs-widgets-container"
          ref={widgetsContainerRef}
          className="builderjs-widgets-container"
          style={{ display: isLoading ? 'none' : 'flex' }}
        />

        {/* Settings Panel - Far Right Side */}
        <div 
          id="builderjs-settings-container"
          ref={settingsContainerRef}
          className="builderjs-settings-container"
          style={{ display: isLoading ? 'none' : 'block' }}
        />
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <div className="builderjs-modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="builderjs-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Save Template</h3>
            <p>Please enter a name for your template:</p>
            <input
              type="text"
              className="builderjs-modal-input"
              placeholder="Template Name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSaveModalSave();
                }
              }}
              autoFocus
            />
            <div className="builderjs-modal-actions">
              <button
                className="builderjs-btn builderjs-btn-secondary"
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </button>
              <button
                className="builderjs-btn builderjs-btn-primary"
                onClick={handleSaveModalSave}
                disabled={!templateName.trim()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuilderJSEmailBuilder;

